import { keyframes } from '@emotion/react';
import type { SxProps, Theme } from '@mui/material';
import { Box } from '@mui/material';
import { useId } from 'react';

import logoSmall from '../Logo/logos/svg/logo-small.svg';

const searchlight = keyframes`
  0%, 100% { transform: translate(0, -27%); }
  11.5% { transform: translate(17%, -19%); }
  21.1% { transform: translate(27%, -7%); }
  33.8% { transform: translate(19%, 12%); }
  43.7% { transform: translate(11%, 26%); }
  55% { transform: translate(-7%, 22%); }
  64.2% { transform: translate(-21%, 17%); }
  74.7% { transform: translate(-27%, 1%); }
  84.4% { transform: translate(-18%, -12%); }
  93.8% { transform: translate(-10%, -25%); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
`;

const reveal = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const igniteFirstArm = keyframes`
  0%, 12% { opacity: 0.12; }
  18%, 92% { opacity: 1; }
  100% { opacity: 0.12; }
`;

const igniteSecondArm = keyframes`
  0%, 32% { opacity: 0.12; }
  38%, 92% { opacity: 1; }
  100% { opacity: 0.12; }
`;

const igniteThirdArm = keyframes`
  0%, 52% { opacity: 0.12; }
  58%, 92% { opacity: 1; }
  100% { opacity: 0.12; }
`;

const igniteFourthArm = keyframes`
  0%, 72% { opacity: 0.12; }
  78%, 92% { opacity: 1; }
  100% { opacity: 0.12; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const reactorBreath = keyframes`
  0%, 100% { opacity: 0.55; transform: scale(0.96); }
  50% { opacity: 1; transform: scale(1.03); }
`;

const reactorWave = keyframes`
  0% { opacity: 0.5; transform: scale(0.55); }
  75%, 100% { opacity: 0; transform: scale(1.55); }
`;

export type LoadingLogoVariant =
  | 'breathingReactor'
  | 'energyScanner'
  | 'energyTrace'
  | 'fourArmIgnition'
  | 'radarSweep';

interface Props {
  delayMs?: number;
  fullWidth?: boolean;
  label?: string;
  size?: number | string;
  sx?: SxProps<Theme>;
  variant?: LoadingLogoVariant;
}

export function LoadingLogo({
  delayMs = 0,
  fullWidth = false,
  label = 'Loading',
  size = '3rem',
  sx,
  variant = 'energyScanner',
}: Props) {
  const instanceId = useId().replace(/:/g, '');
  const armClipPathIds = Array.from(
    { length: 4 },
    (_, index) => `loading-logo-arm-${index}-${instanceId}`
  );
  const glowGradientId = `loading-logo-glow-gradient-${instanceId}`;
  const maskId = `loading-logo-mask-${instanceId}`;
  const radarBlurId = `loading-logo-radar-blur-${instanceId}`;
  const radarMaskId = `loading-logo-radar-mask-${instanceId}`;
  const scannerGradientId = `loading-logo-scanner-gradient-${instanceId}`;
  const traceFilterId = `loading-logo-trace-filter-${instanceId}`;
  const traceMaskId = `loading-logo-trace-mask-${instanceId}`;

  return (
    <Box
      aria-label={label}
      data-loading-logo-variant={variant}
      role="progressbar"
      sx={[
        {
          alignItems: 'center',
          animation: delayMs > 0 ? `${reveal} 180ms ease-out ${delayMs}ms both` : undefined,
          display: 'flex',
          flexShrink: 0,
          height: size,
          justifyContent: 'center',
          width: fullWidth ? '100%' : size,
          '& .LoadingLogo-searchlight': {
            animation: `${searchlight} 2.4s linear infinite`,
            transformBox: 'view-box',
          },
          '& .LoadingLogo-arm-0': {
            animation: `${igniteFirstArm} 3s ease-in-out infinite`,
          },
          '& .LoadingLogo-arm-1': {
            animation: `${igniteSecondArm} 3s ease-in-out infinite`,
          },
          '& .LoadingLogo-arm-2': {
            animation: `${igniteThirdArm} 3s ease-in-out infinite`,
          },
          '& .LoadingLogo-arm-3': {
            animation: `${igniteFourthArm} 3s ease-in-out infinite`,
          },
          '& .LoadingLogo-radar': {
            animation: `${rotate} 2s linear infinite`,
            transformBox: 'view-box',
            transformOrigin: 'center',
          },
          '& .LoadingLogo-reactor-mark': {
            animation: `${reactorBreath} 2s ease-in-out infinite`,
            transformBox: 'fill-box',
            transformOrigin: 'center',
          },
          '& .LoadingLogo-reactor-wave': {
            animation: `${reactorWave} 2s ease-out infinite`,
            transformBox: 'fill-box',
            transformOrigin: 'center',
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            '& .LoadingLogo-effect': {
              opacity: 0,
            },
            '& .LoadingLogo-base': {
              animation: `${pulse} 1.5s ease-in-out infinite`,
            },
            '& .LoadingLogo-motion': {
              animation: 'none',
            },
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        aria-hidden="true"
        component="svg"
        height={size}
        overflow="visible"
        viewBox="0 0 200 201"
        width={size}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <radialGradient id={glowGradientId}>
            <stop offset="0%" stopColor="#F89728" stopOpacity="0.38" />
            <stop offset="45%" stopColor="#F89728" stopOpacity="0.16" />
            <stop offset="100%" stopColor="#F89728" stopOpacity="0" />
          </radialGradient>
          <radialGradient id={scannerGradientId}>
            <stop offset="0%" stopColor="white" />
            <stop offset="38%" stopColor="white" />
            <stop offset="100%" stopColor="black" />
          </radialGradient>
          <filter id={radarBlurId} x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="5" />
          </filter>
          <filter id={traceFilterId} x="-20%" y="-20%" width="140%" height="140%">
            <feMorphology in="SourceAlpha" operator="dilate" radius="3" result="dilated" />
            <feComposite in="dilated" in2="SourceAlpha" operator="out" result="outline" />
            <feFlood floodColor="#F89728" result="orange" />
            <feComposite in="orange" in2="outline" operator="in" result="orangeOutline" />
            <feGaussianBlur in="orangeOutline" stdDeviation="1.5" result="outlineGlow" />
            <feMerge>
              <feMergeNode in="outlineGlow" />
              <feMergeNode in="orangeOutline" />
            </feMerge>
          </filter>
          <mask id={maskId} maskUnits="userSpaceOnUse" x="0" y="0" width="200" height="201">
            <rect width="200" height="201" fill="black" />
            <g className="LoadingLogo-motion LoadingLogo-searchlight">
              <circle
                className="LoadingLogo-effect"
                cx="100"
                cy="100"
                fill={`url(#${scannerGradientId})`}
                r="95"
              />
            </g>
          </mask>
          <mask id={traceMaskId} maskUnits="userSpaceOnUse" x="0" y="0" width="200" height="201">
            <rect width="200" height="201" fill="black" />
            <g className="LoadingLogo-motion LoadingLogo-searchlight">
              <circle
                className="LoadingLogo-effect"
                cx="100"
                cy="100"
                fill={`url(#${scannerGradientId})`}
                r="68"
              />
            </g>
          </mask>
          <mask id={radarMaskId} maskUnits="userSpaceOnUse" x="0" y="0" width="200" height="201">
            <rect width="200" height="201" fill="black" />
            <g className="LoadingLogo-motion LoadingLogo-radar">
              <path
                className="LoadingLogo-effect"
                d="M100 100 L62 -56 A161 161 0 0 1 166 -46 Z"
                fill="white"
                filter={`url(#${radarBlurId})`}
              />
            </g>
          </mask>
          {armClipPathIds.map((clipPathId, index) => (
            <clipPath id={clipPathId} key={clipPathId}>
              <rect
                x={index === 1 || index === 2 ? 100 : 0}
                y={index > 1 ? 100 : 0}
                width="100"
                height="101"
              />
            </clipPath>
          ))}
        </defs>

        {variant === 'energyScanner' && (
          <g className="LoadingLogo-effect LoadingLogo-motion LoadingLogo-searchlight">
            <circle cx="100" cy="100" fill={`url(#${glowGradientId})`} r="105" />
          </g>
        )}

        {variant === 'radarSweep' && (
          <g className="LoadingLogo-effect LoadingLogo-motion LoadingLogo-radar" opacity="0.16">
            <path
              d="M100 100 L62 -56 A161 161 0 0 1 166 -46 Z"
              fill="#F89728"
              filter={`url(#${radarBlurId})`}
            />
          </g>
        )}

        {variant === 'breathingReactor' && (
          <g fill="none" stroke="#F89728" strokeWidth="5">
            <circle
              className="LoadingLogo-effect LoadingLogo-motion LoadingLogo-reactor-wave"
              cx="100"
              cy="100"
              r="42"
            />
            <circle
              className="LoadingLogo-effect LoadingLogo-motion LoadingLogo-reactor-wave"
              cx="100"
              cy="100"
              r="42"
              style={{ animationDelay: '-1s' }}
            />
          </g>
        )}

        <image
          className={`LoadingLogo-base${
            variant === 'breathingReactor' ? ' LoadingLogo-motion LoadingLogo-reactor-mark' : ''
          }`}
          height="201"
          href={logoSmall}
          opacity={variant === 'breathingReactor' ? '0.55' : '0.14'}
          width="200"
        />

        {variant === 'energyScanner' && (
          <image
            className="LoadingLogo-effect"
            height="201"
            href={logoSmall}
            mask={`url(#${maskId})`}
            width="200"
          />
        )}

        {variant === 'fourArmIgnition' &&
          armClipPathIds.map((clipPathId, index) => (
            <image
              className={`LoadingLogo-arm-${index} LoadingLogo-effect LoadingLogo-motion`}
              clipPath={`url(#${clipPathId})`}
              height="201"
              href={logoSmall}
              key={clipPathId}
              width="200"
            />
          ))}

        {variant === 'radarSweep' && (
          <image
            className="LoadingLogo-effect"
            height="201"
            href={logoSmall}
            mask={`url(#${radarMaskId})`}
            width="200"
          />
        )}

        {variant === 'energyTrace' && (
          <image
            className="LoadingLogo-effect"
            filter={`url(#${traceFilterId})`}
            height="201"
            href={logoSmall}
            mask={`url(#${traceMaskId})`}
            width="200"
          />
        )}
      </Box>
    </Box>
  );
}
